import React, { useState, useEffect, ReactNode } from 'react';
import moment from "moment";
import { useForm, FormProvider, Controller, Form } from "react-hook-form";
import {
    Button,
    Box,
    Grid,
    Container,
} from "@comfama/comfama-ui";

// services //
import { saveForm, getToken } from "../../../services/formServices";

// Components //
import { FormInputLib } from "../../atoms/form-input/FormInputLib";
import { FormEnterpriseMentorshipQuery } from "./FormEnterpriseMentorshipQuery";
import { formMentoriaTalentoAdapter } from "../form-mentoria-talento/adapters/formMentoriaTalentoAdapter";
import Modal from "../../molecules/modal/modal";
import FormHead from "../../molecules/form-head/FormHead";
import Loading from "../../atoms/loading/loading";
import ArrowRigth from "../../atoms/icons/ArrowRigth";
// Utils //
import contentfulRenderOptions from "../../../utils/contentfulRenderOption";
import rawToReact from "../../../utils/rawToReact";

import "./FormEnterpriseMentorship.scss";

interface IFormEnterpriseMentorshipProps {
    [key: string]: { contentfulId: string; answer: string };
}

interface IModalContent {
    title: string;
    image: string;
    textButton: string;
    url: string;
    body: ReactNode;
}
interface FormEnterpriseMentorshipProps {
    initialCompanyName?: string;
    initialDocumentNumber?: string;
}

const FormEnterpriseMentorship = ({
        initialCompanyName,
        initialDocumentNumber
    }: FormEnterpriseMentorshipProps) => {

    // Query de GraphQL para obtener los datos del formulario
    const formData = FormEnterpriseMentorshipQuery()
    const adapterData = formMentoriaTalentoAdapter(formData);
    const contentfulData = formData.form.nodes[0];
    const modalSuccess = formData.modalSuccess.nodes[0];
    const modalUnsuccess = formData.modalUnsuccess.nodes[0];


    const objForm = {};

    adapterData.sections.forEach((item) => {
        item.fields.forEach((subItem) => {
            objForm[subItem.name] = {
                contentfulId: subItem.name,
                answer: "",
            };
        });
    });

    const methods = useForm<IFormEnterpriseMentorshipProps>({
        defaultValues: {
            ...objForm,
            registerDate: {
                contentfulId: "registerDate",
                answer: moment(Date.now()).format("DD-MM-YYYY"),
            },
            companyName: {
                contentfulId: "companyName",
                answer: initialCompanyName ?? "",
            },
            documentNumber: {
                contentfulId: "documentNumber",
                answer: initialDocumentNumber ?? "",
            },
            fullName: {
                contentfulId: "fullName", answer: ""},
            companyRole: {
                contentfulId: "companyRole", answer: ""},
            email: {
                contentfulId: "email", answer: ""},
            mobile: {
                contentfulId: "mobile", answer: ""},
            dataAuthorization: {
                contentfulId: "dataAuthorization", answer: ""},
        },
    });
    
    const {
        handleSubmit,
        watch,
        trigger,
        getValues,
        setError,
        control,
    } = methods;

    const [activeStep, setActiveStep] = useState<number>(0);
    // Estado para confirmar que el step esta completamente diligenciado.
    const [isStepValid, setIsStepValid] = useState<boolean>(false);
    const [modalContent, setModalContent] = useState<IModalContent>();
    const [enableTreatment, setEnableTreatment] = useState<boolean>(false);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    // Estado para abrir el modal de confirmación
    const [isOpenModal, setIsOpenModal] = useState<boolean>(false);
    // Función para verificar si todos los campos visibles están dilingeciados //pendiente implementar useMemo() para performace
    const isStepComplete = () => {
        const fields = adapterData.sections[activeStep].fields;

        return fields
            .filter((field) => !field.disabled)
            .map((field) => field.name);
    };

    const value = watch(isStepComplete());

    //Validad activa el boton continuar si los campos estan diligenciados todos
    useEffect(() => {
        const fn = async () => {
            const validateTrigger = await trigger(isStepComplete());
            setIsStepValid(validateTrigger);
        };
        fn();
    }, [value, activeStep]);

    useEffect(() => {
        const subscription = watch((values, { name }) => {
            if (name === "dataAuthorization.answer") {
                setEnableTreatment(values.dataAuthorization?.answer === "No acepto la política de datos");
            }
        });
        return () => subscription.unsubscribe();
    }, [watch]);

    // Validacion input email- mobile - nit
    const changeStepper = () => {

        const regexEmail = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
        const validationMobile = getValues("mobile.answer");
        const nit = getValues("documentNumber.answer");

        if (!regexEmail.test(getValues("email.answer"))){
            return setError("email", {type: "manual", message: "* El correo no es válido"});
        }
        if (validationMobile.length !== 10){
            return setError("mobile", {type: "manual", message: "* El número no es válido."})
        }
        if (nit.length !== 9) {
            return setError("documentNumber", {type: "manual", message: "* El NIT no es válido."});
        }
        setActiveStep(activeStep + 1);
    };

    const maxLengthField = (type: string) => {
        switch (type) {
            case "mobile":
                return 10; 
            case "phone":
                return 10;         
            case "documentNumber":
                return 9;
            default:
                return 170;
        }
    };

    // Guarda en un estado con el contenido de los modales que se renderizan al finalizar el formulario
    const handleModalContent = (status: number) => {
        if (status === 200) {
            setModalContent({
                title: modalSuccess.tituloPrincipal,
                image: modalSuccess.imagenFormulario.file.url,
                textButton: modalSuccess.tituloBoton,
                url: modalSuccess.redireccionBoton,
                body: rawToReact(modalSuccess.descripcionFormulario.raw,contentfulRenderOptions),
            });
            dataLayerConfirmation();
        } else {
            setModalContent({
                title: modalUnsuccess.tituloPrincipal,
                image: modalUnsuccess.imagenFormulario.file.url,
                textButton: modalUnsuccess.tituloBoton,
                url: modalUnsuccess.redireccionBoton,
                body: rawToReact(modalUnsuccess.descripcionFormulario.raw,contentfulRenderOptions),
            });
        }
    };

    const dataLayerConfirmation = () => {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
            'event': 'EnvioExitosoFormMentoriaEmpresarial',
            'envioformulario': `empleo_confirmacion_envio_exitoso_formulario_mentoria_empresarial`,
        });
    }

    const onSubmit = async () => {
        setIsLoading(true);
        try {

            const valuesForm = getValues();
            const formMentoriaEmp = { "idForm": 23, "answers" : Object.values(valuesForm)};
            const token = await getToken();
            const response = await saveForm(formMentoriaEmp, token);
            console.log("fin formulario: ", response.data);
            handleModalContent(response.data.status);
            setIsLoading(false);
            setIsOpenModal(true);
        } catch (error) {
            setIsOpenModal(true);
            setIsLoading(false);
        }
    };

    return (
        <section className='o-form-enterprise-mentorship'>
            <Container maxWidth="md">
                <div
                    // Asigna como nombre de la Clase el meta titulo del formulario
                    className={`o-form-${contentfulData.metaTitulo}`}
                >
                    <div className={`o-form-${contentfulData.metaTitulo}__cont`}>
                        <FormHead
                            tituloFormulario={contentfulData.tituloFormulario}
                        />
                        {adapterData.mainDescription}
                        <div className={`o-form-${contentfulData.metaTitulo}__cont-form`}>    
                            {/* Renderiza el cuerpo del formulario */}
                            <FormProvider {...methods}>
                                <form onSubmit={handleSubmit(onSubmit)}>
                                    <Grid container spacing={3} className="form-grid">
                                        {/* Renderiza los campos del step activo */}
                                        {adapterData.sections[activeStep].fields.map((subItem) => (
                                            <Grid
                                                item
                                                xs={12}
                                                md={subItem.type === "checkbox" ? 12 : 6}
                                                key={subItem.name}
                                            >
                                                <FormInputLib
                                                    attributes={{ ...subItem, maxLength: maxLengthField(subItem.name) }}
                                                    control={control}
                                                    tagUseForm={".answer"}
                                                    alertMessage={enableTreatment}
                                                />
                                            </Grid>
                                        ))}
                                    </Grid>
                                    <Box pt={8} textAlign={"center"} marginBottom={"10px"}>
                                        <Button
                                            variant="contained"
                                            size="small"
                                            disabled={!isStepValid || enableTreatment}
                                            onClick={
                                                activeStep === adapterData.sections.length - 1
                                                    ? handleSubmit(onSubmit)
                                                    : () => changeStepper()
                                            }
                                            sx={{ ml: 1 }}
                                        >
                                            {activeStep === adapterData.sections.length - 1
                                                ? "Enviar"
                                                : "Siguiente"}
                                            <ArrowRigth />
                                        </Button>
                                    </Box>
                                </form>
                            </FormProvider>
                        </div>
                    </div>
                    {isLoading && (
                        <div className="loading">
                            <Loading />
                        </div>
                    )}
                    {isOpenModal && (
                        <div className="modal-Container">
                            <Modal
                                image={modalContent?.image ? modalContent.image : ""}
                                title={modalContent?.title ? modalContent.title : ""}
                                textButton={
                                    modalContent?.textButton ? modalContent.textButton : ""
                                }
                                url={modalContent?.url ? modalContent.url : ""}
                            >
                                <div
                                    className={`o-form-${contentfulData.metaTitulo}__content-modal`}
                                >
                                    <p>{ modalContent?.body } </p>
                                </div>
                            </Modal>
                        </div>
                    )}
                </div>
            </Container>
        </section>
    );
};

export default FormEnterpriseMentorship;